import { Component, ElementRef, NgZone } from '@angular/core';
import { HeroMotifBase, RGB } from '../hero-motif/hero-motif.base';

const LOOP = 40;
const W0 = Math.PI * 2 / LOOP;

/** A car's lights on an avenue, in metres: the avenue, its lane's direction, its place along it, its speed. */
interface Car {
  road: number;
  dir: 1 | -1;
  z: number;
  speed: number;
  /** Out only while the counter is strong enough for its rank; fading in and out. */
  rank: number;
  shown: number;
}

/** One thing in the city, drawn far to near. */
interface Thing {
  z: number;
  draw: () => void;
}

/**
 * Tokyo's hero board (2026-10-17), after the owner's photograph: the city at night from high on
 * Roppongi Hills, in true perspective (a camera 250 m up, every object placed in metres and projected).
 * A deep navy sky; the city's towers and blocks to the horizon, their windows lit; the bay off to the
 * right; Tokyo Tower in the middle in its orange light, its lattice in 3D, the white decks. Car lights
 * stream along the avenues (more on the charge), the aircraft lights on the tall towers and the tower's
 * mast blink. The page's wallpaper is the city's lights, soft and scattered. Sized by the stage's height.
 */
@Component( {
  selector: 'app-hero-tokyo',
  template: '<canvas #canvas class="htok-canvas" aria-hidden="true"></canvas>',
  styles: [ ':host { display: block; position: absolute; inset: 0; overflow: hidden; pointer-events: none; }',
    '.htok-canvas { display: block; position: absolute; inset: 0; }' ],
} )
export class HeroTokyoComponent extends HeroMotifBase {

  /** (owner: Tokyo stuttered) the sky and the ground baked as one layer: they are drawn one after the other. */
  private base: HTMLCanvasElement | null = null;
  private city: HTMLCanvasElement | null = null;
  /** The city before the tower, drawn over it. */
  private cityFront: HTMLCanvasElement | null = null;
  /** The windows at full brightness, behind and before the tower, shown on the charge (owner). */
  private winBack: HTMLCanvasElement | null = null;
  private winFront: HTMLCanvasElement | null = null;
  /**
   * The tower's iron, near black in the night (owner), baked once in its colour and kept only as large as the
   * tower's own box on screen (it was three full layers a frame, two of them empty).
   */
  private towerLayer: HTMLCanvasElement | null = null;
  private towerRect: [ number, number, number, number ] = [ 0, 0, 0, 0 ];
  /** The rows the city's layers and the windows' layers hold (page pixels): only those are drawn. */
  private cityTop = 0;
  private frontTop = 0;
  private winTop = 0;
  private winBottom = 0;
  /** Soft round glows made once (the cars' lamps, the aircraft lights), drawn at a size and a strength. */
  private glowSprites = new Map<string, HTMLCanvasElement>();
  /**
   * What the layers were baked for (size and density) and the camera they were baked with: a re-layout at
   * the same size keeps them, and while the stage is being resized they are drawn scaled until it settles.
   */
  private bakedFor = '';
  private pendingFor = '';
  private pendingAt = 0;
  private bW = 1;
  private bH = 1;
  private cF = 0;
  private cHz = 0;
  private cVx = 0;
  /** The Diamond Veil's LED points on screen: x, y, how high up the tower (0..1), size. */
  private veil: [ number, number, number, number ][] = [];
  private veilSprites: HTMLCanvasElement[] = [];
  /** Where the tower's light is in its round of colours (0..4). */
  private hue = 0;
  /** The main deck's band of windows on screen: x0, y0, x1, y1, and pixels per metre there. */
  private deckBand: [ number, number, number, number, number ] | null = null;
  /** The tower's middle on screen and the reach of its glow. */
  private towerGlow: [ number, number, number ] | null = null;
  /** The mast's gold light on screen: x, foot y, top y, width at the foot and at the top. */
  private mastBar: [ number, number, number, number, number ] | null = null;
  private cars: Car[] = [];
  /** The red aircraft lights on the tallest roofs and the tower's mast, on screen, with a phase each. */
  private blinks: [ number, number, number, number ][] = [];
  private scaleK = 1;
  // the camera: eye height (m), focal length and horizon (px), the vanishing point's x (px)
  private eye = 250;
  private F = 0;
  private hz = 0;
  private vx = 0;
  /** The city's blocks are new on every visit (owner): this visit's seed. */
  private citySeed = Math.floor( Math.random() * 1e6 );
  /** The avenues' x (m) the cars drive along. */
  private roads = [ -300, 270 ];

  constructor( zone: NgZone, host: ElementRef<HTMLElement> ) {
    super( zone, host );
    this.loop = LOOP;
    this.ease = 1.2;
  }

  protected colorsChanged(): void {
    this.bakedFor = '';
    this.veilSprites = [];
  }

  /** Project a point: x across (metres from the axis), y up, z away from us. */
  private P( x: number, y: number, z: number ): [ number, number ] {
    return [ this.cVx + this.cF * x / z, this.cHz - this.cF * ( y - this.eye ) / z ];
  }

  protected build(): void {
    const W = this.width;
    const H = this.height;
    this.scaleK = Math.max( 0.55, Math.min( 1.25, H / 760 ) );
    this.F = 1.6 * H;
    this.hz = 0.26 * H;
    this.vx = W / 2;
    const r = this.seededLocal( 29 );
    this.cars = [];
    for ( let i = 0; i < 220; i++ ) {
      this.cars.push( { road: i % this.roads.length, dir: r() < 0.5 ? 1 : -1, z: 480 + r() * 4200, speed: 11 + r() * 9, rank: r(), shown: 0 } );
    }
  }

  protected paint( ctx: CanvasRenderingContext2D, dt: number ): void {
    const W = this.width;
    const H = this.height;
    if ( this.frozen ) {
      // the page's wallpaper: the city's lights alone, soft, scattered down the page
      this.drawWallLights( ctx );
      return;
    }
    const scale = ctx.getTransform().a, key = W + 'x' + H + '@' + scale, now = performance.now();
    if ( this.bakedFor !== key ) {
      if ( this.pendingFor !== key ) { this.pendingFor = key; this.pendingAt = now; }
      if ( !this.city || !this.bakedFor || this.still || now - this.pendingAt > 300 ) {
        this.bake( scale );
        this.bakedFor = key;
      }
    }
    if ( !this.city || !this.base ) { return; }
    // while the stage is being resized, the layers baked for the old size, scaled to the new one
    if ( W !== this.bW || H !== this.bH ) { ctx.scale( W / this.bW, H / this.bH ); }
    const bw = this.bW, bh = this.bH;
    ctx.drawImage( this.base, 0, 0, bw, bh );
    this.drawCars( ctx, dt );
    this.drawRows( ctx, this.city, this.cityTop, bh );
    // the tower's colour: about 40 s for each at rest, about 15 s on the charge
    if ( dt > 0 ) { this.hue = ( this.hue + dt * ( 0.025 + 0.04 * this.energy ) ) % 4; }
    const lit = 0.12 + 0.88 * this.energy;
    if ( this.winBack ) { ctx.globalAlpha = 0.03 + 0.97 * this.energy; this.drawRows( ctx, this.winBack, this.winTop, this.winBottom ); ctx.globalAlpha = 1; }
    this.drawTower( ctx );
    if ( this.cityFront ) { this.drawRows( ctx, this.cityFront, this.frontTop, bh ); }
    if ( this.winFront ) { ctx.globalAlpha = lit; this.drawRows( ctx, this.winFront, this.winTop, this.winBottom ); ctx.globalAlpha = 1; }
    this.drawBlinks( ctx );
  }

  /** The rows y0 to y1 of a full-size layer (page pixels of the size it was baked for). */
  private drawRows( ctx: CanvasRenderingContext2D, layer: HTMLCanvasElement, y0: number, y1: number ): void {
    const sc = layer.width / this.bW, a = Math.max( 0, Math.floor( y0 ) ), b = Math.min( this.bH, Math.ceil( y1 ) );
    if ( b - a < 1 ) { return; }
    ctx.drawImage( layer, 0, a * sc, layer.width, ( b - a ) * sc, 0, a, this.bW, b - a );
  }

  /** A soft round glow in one colour, its heart and its fall as the old per-frame gradients had them. */
  private glowSprite( key: string, stops: [ number, string ][] ): HTMLCanvasElement {
    let c = this.glowSprites.get( key );
    if ( !c ) {
      c = document.createElement( 'canvas' );
      c.width = c.height = 64;
      const g = c.getContext( '2d' )!, gr = g.createRadialGradient( 32, 32, 0, 32, 32, 32 );
      for ( const [ at, col ] of stops ) { gr.addColorStop( at, col ); }
      g.fillStyle = gr;
      g.fillRect( 0, 0, 64, 64 );
      this.glowSprites.set( key, c );
    }
    return c;
  }

  /**
   * The traffic: headlights coming toward us, tail lights going away, on the avenues between the blocks;
   * the nearer buildings hide them (the city is drawn over them, and a farther building never reaches
   * down over a nearer stretch of road). More of them on the charge.
   */
  private drawCars( ctx: CanvasRenderingContext2D, dt: number ): void {
    const share = 0.35 + 0.65 * this.energy;
    const F = this.cF;
    for ( const c of this.cars ) {
      const want = c.rank < share ? 1 : 0;
      c.shown = dt > 0 ? c.shown + ( want - c.shown ) * Math.min( 1, dt * 1.5 ) : want;
      if ( dt > 0 ) {
        c.z += c.dir * c.speed * dt * ( 0.8 + 0.6 * this.energy );
        if ( c.z > 4700 ) { c.z = 480; } else if ( c.z < 480 ) { c.z = 4700; }
      }
      if ( c.shown < 0.02 ) { continue; }
      const x = this.roads[ c.road ] + ( c.dir > 0 ? 7 : -7 );
      const [ px, py ] = this.P( x, 0.8, c.z );
      const s = F / c.z;
      const col: RGB = c.dir > 0 ? [ 255, 70, 60 ] : [ 255, 244, 220 ];
      const rr = Math.max( 0.7, s * 1.6 );
      // (owner: Tokyo stuttered) one glow made once per colour, not a new gradient for every car each frame
      const img = this.glowSprite( c.dir > 0 ? 'tail' : 'head', [ [ 0, this.rgba( col, 0.95 ) ], [ 0.35, this.rgba( col, 0.4 ) ], [ 1, this.rgba( col, 0 ) ] ] );
      ctx.globalAlpha = c.shown;
      ctx.drawImage( img, px - rr * 2.2, py - rr * 2.2, rr * 4.4, rr * 4.4 );
    }
    ctx.globalAlpha = 1;
  }

  /**
   * The tower's light as the real one is lit (owner, after photographs): its own orange Landmark Light at
   * rest; on the charge the Diamond Veil comes on, rows of LED points along its girders in the theme's
   * colours, tier by tier from the arch upward, while the orange dims behind them.
   */
  private drawTower( ctx: CanvasRenderingContext2D ): void {
    const e = this.energy;
    // the iron near black in the night (owner); its light is the veil's
    if ( this.towerLayer ) {
      const [ x, y, w, h ] = this.towerRect, sc = this.towerLayer.width / Math.max( 1, w );
      ctx.drawImage( this.towerLayer, 0, 0, this.towerLayer.width, h * sc, x, y, w, h );
    }
    // the Diamond Veil, always lit like neon (owner's photograph): one deep colour of the theme's at a
    // time, its glow round the dark iron; on the charge it turns through the circle's colours, brighter
    if ( this.veil.length ) {
      const [ , peri, pink, green ] = this.five();
      // saturated, like neon (owner): each colour pushed away from its grey, a touch of violet
      const sat = ( c: RGB ): RGB => { const m = ( c[ 0 ] + c[ 1 ] + c[ 2 ] ) / 3; return c.map( v => Math.max( 0, Math.min( 255, m + ( v - m ) * 1.9 ) ) ) as RGB; };
      // no light blue (owner): a violet from the periwinkle at rest, then the pink, the green and a hot magenta
      const ring = [ this.mix( peri, [ 150, 40, 255 ], 0.5 ), pink, green, this.mix( pink, [ 255, 30, 150 ], 0.45 ) ].map( c => sat( c ) );
      if ( !this.veilSprites.length ) {
        this.veilSprites = ring.map( c => {
          const cv = document.createElement( 'canvas' );
          cv.width = cv.height = 24;
          const vg = cv.getContext( '2d' )!;
          const gr = vg.createRadialGradient( 12, 12, 0, 12, 12, 12 );
          // the light's own colour, hardly any white even at its heart (owner)
          gr.addColorStop( 0, this.rgba( this.mix( c, [ 255, 255, 255 ], 0.06 ), 1 ) );
          gr.addColorStop( 0.2, this.rgba( c, 1 ) );
          gr.addColorStop( 0.45, this.rgba( c, 0.3 ) );
          gr.addColorStop( 1, this.rgba( c, 0 ) );
          vg.fillStyle = gr;
          vg.fillRect( 0, 0, 24, 24 );
          return cv;
        } );
      }
      // turning slowly through its colours all the time (owner), a little quicker on the charge
      const u = this.hue % ring.length, i0 = Math.floor( u ) % ring.length, fr = u - Math.floor( u );
      const cNow = this.mix( ring[ i0 ], ring[ ( i0 + 1 ) % ring.length ], fr );
      if ( this.towerGlow ) {
        const [ gx, gy, gr0 ] = this.towerGlow;
        const gg = ctx.createRadialGradient( gx, gy, 0, gx, gy, gr0 );
        gg.addColorStop( 0, this.rgba( cNow, 0.16 + 0.24 * e ) );
        gg.addColorStop( 0.5, this.rgba( cNow, 0.07 ) );
        gg.addColorStop( 1, this.rgba( cNow, 0 ) );
        ctx.fillStyle = gg;
        ctx.fillRect( gx - gr0, gy - gr0, gr0 * 2, gr0 * 2 );
      }
      ctx.save();
      // laid over, not added: overlapping points keep their colour instead of burning white (owner)
      ctx.globalCompositeOperation = 'source-over';
      // brighter on the charge (owner), the points swelling a little
      const bright = 0.62 + 0.38 * e;
      for ( const [ part, img ] of [ [ 1 - fr, this.veilSprites[ i0 ] ], [ fr, this.veilSprites[ ( i0 + 1 ) % ring.length ] ] ] as [ number, HTMLCanvasElement ][] ) {
        if ( part < 0.02 ) { continue; }
        ctx.globalAlpha = part * bright;
        for ( const [ x, y, , size ] of this.veil ) {
          const r = size * ( 3 + 1.3 * e );
          ctx.drawImage( img, x - r, y - r, r * 2, r * 2 );
        }
      }
      // the main deck's windows, a band of the tower's light, its mullions dark
      if ( this.deckBand ) {
        const [ x0, y0, x1, y1, ppm ] = this.deckBand;
        ctx.globalAlpha = 0.7 + 0.3 * e;
        ctx.fillStyle = this.rgba( this.mix( cNow, [ 255, 255, 255 ], 0.08 ), 1 );
        ctx.fillRect( x0, y0, x1 - x0, y1 - y0 );
        ctx.globalCompositeOperation = 'source-over';
        ctx.fillStyle = 'rgba(20,18,34,0.5)';
        for ( let xx = x0; xx < x1; xx += ppm * 2.4 ) { ctx.fillRect( xx, y0, Math.max( 0.5, ppm * 0.4 ), y1 - y0 ); }
        ctx.globalAlpha = 1;
      }
      // the mast: a bar of light tapering up, its glow round it (owner's photograph)
      if ( this.mastBar ) {
        const [ mx, y0, y1, w0, w1 ] = this.mastBar;
        const lg = ctx.createLinearGradient( 0, y0, 0, y1 );
        // in the tower's own colour (owner), brightest toward its top
        lg.addColorStop( 0, this.rgba( cNow, 1 ) );
        lg.addColorStop( 0.6, this.rgba( this.mix( cNow, [ 255, 255, 255 ], 0.06 ), 1 ) );
        lg.addColorStop( 0.92, this.rgba( this.mix( cNow, [ 255, 255, 255 ], 0.12 ), 1 ) );
        lg.addColorStop( 1, this.rgba( cNow, 0.2 ) );
        for ( const [ k, a ] of [ [ 4, 0.18 ], [ 2, 0.35 ], [ 1, 1 ] ] ) {
          ctx.globalAlpha = a;
          ctx.fillStyle = lg;
          ctx.beginPath();
          ctx.moveTo( mx - w0 * k / 2, y0 ); ctx.lineTo( mx - w1 * k / 2, y1 ); ctx.lineTo( mx + w1 * k / 2, y1 ); ctx.lineTo( mx + w0 * k / 2, y0 );
          ctx.closePath();
          ctx.fill();
        }
        ctx.globalAlpha = 1;
      }
      ctx.restore();
    }
  }

  /** The aircraft lights: red, slow, each on its own beat; on the charge they all come on (owner). */
  private drawBlinks( ctx: CanvasRenderingContext2D ): void {
    const t = this.time * W0;
    for ( const [ x, y, ph, size ] of this.blinks ) {
      const a = Math.max( this.energy, Math.pow( Math.max( 0, Math.sin( t * 10 + ph ) ), 3 ) );
      if ( a < 0.03 ) { continue; }
      const rr = size * ( 2 + a );
      ctx.globalAlpha = a;
      ctx.drawImage( this.glowSprite( 'blink', [ [ 0, 'rgba(255,120,110,1)' ], [ 0.3, 'rgba(255,40,40,0.5)' ], [ 1, 'rgba(255,40,40,0)' ] ] ), x - rr, y - rr, rr * 2, rr * 2 );
    }
    ctx.globalAlpha = 1;
  }

  /** The wallpaper: the city's lights as soft points in the theme's colours, scattered down the page. */
  private drawWallLights( ctx: CanvasRenderingContext2D ): void {
    const W = this.width;
    const H = this.height;
    const k = this.scaleK;
    const [ , peri, pink, green, cyan ] = this.five();
    const cols: RGB[] = [ peri, pink, green, cyan, [ 255, 214, 150 ] ];
    const r = this.seededLocal( 5 );
    const n = Math.max( 10, Math.round( W * H / 22000 ) );
    for ( let i = 0; i < n; i++ ) {
      const x = r() * W, y = r() * H, rad = ( 3 + r() * 8 ) * k, a = 0.25 + r() * 0.4;
      const col = cols[ Math.floor( r() * cols.length ) ];
      const g = ctx.createRadialGradient( x, y, 0, x, y, rad );
      g.addColorStop( 0, this.rgba( col, a ) );
      g.addColorStop( 0.5, this.rgba( col, a * 0.45 ) );
      g.addColorStop( 1, this.rgba( col, 0 ) );
      ctx.fillStyle = g;
      ctx.fillRect( x - rad, y - rad, rad * 2, rad * 2 );
    }
  }

  /**
   * The still parts, painted once, far to near, in metres projected: the sky, the ground with its lit
   * streets and the bay, then the city's buildings and Tokyo Tower among them.
   */
  private bake( scale: number ): void {
    const W = this.width;
    const H = this.height;
    // the camera these layers are seen with, kept while they stay on screen
    this.cF = this.F;
    this.cHz = this.hz;
    this.cVx = this.vx;
    this.bW = W;
    this.bH = H;
    this.winTop = H;
    this.winBottom = 0;
    const hz = this.hz;
    const F = this.F;
    const [ navy, peri, pink, green, cyan ] = this.five();
    const white: RGB = [ 255, 255, 255 ];
    const canvas = () => {
      const c = document.createElement( 'canvas' );
      c.width = Math.ceil( W * scale );
      c.height = Math.ceil( H * scale );
      const g2 = c.getContext( '2d' )!;
      g2.scale( scale, scale );
      return { c, g: g2 };
    };
    const P = ( x: number, y: number, z: number ) => this.P( x, y, z );
    let g: CanvasRenderingContext2D;
    let towerMask: HTMLCanvasElement | null = null;
    // the windows' bright layer (owner: dim at rest, full on the charge): each building wipes out the
    // bright windows behind it there, then adds its own
    let wg: CanvasRenderingContext2D | null = null;
    const erase = ( pts: [ number, number ][] ) => {
      if ( !wg ) { return; }
      wg.globalCompositeOperation = 'destination-out';
      wg.fillStyle = '#000';
      wg.beginPath();
      pts.forEach( ( [ x, y ], i ) => i ? wg!.lineTo( x, y ) : wg!.moveTo( x, y ) );
      wg.closePath();
      wg.fill();
      wg.globalCompositeOperation = 'source-over';
    };
    const poly = ( pts: [ number, number ][], fill: string | CanvasGradient ) => {
      g.fillStyle = fill;
      g.beginPath();
      pts.forEach( ( [ x, y ], i ) => i ? g.lineTo( x, y ) : g.moveTo( x, y ) );
      g.closePath();
      g.fill();
    };
    // the night's colours, from the theme's navy and periwinkle
    const skyTop = this.mix( navy, [ 6, 8, 24 ], 0.55 );
    const skyLow = this.mix( this.mix( navy, peri, 0.3 ), [ 60, 70, 120 ], 0.3 );
    const glow = this.mix( skyLow, [ 120, 110, 140 ], 0.35 );
    // the buildings near black (owner: darker)
    const body: RGB = this.mix( navy, [ 6, 6, 12 ], 0.75 );
    const warm: RGB = [ 255, 214, 150 ], whiteLit: RGB = [ 236, 240, 255 ], coolLit = this.mix( peri, white, 0.45 );
    const inAir = ( col: RGB, z: number, strength = 0.85 ) => this.mix( col, glow, ( 1 - Math.exp( -z / 5200 ) ) * strength );
    const halfAt = ( z: number ) => ( W / 2 + 60 ) * z / F;
    // (owner: no sea behind, the city runs on to the sky)
    // higher on the stage and smaller (owner): farther off
    // (owner, later: 100 px lower) nearer, the same size on the stage
    const tower = { x: 0, z: 746 };
    // its size against the real one: 21% larger at 900 m (owner: 10%, then 10% more), the same on screen here
    const TS = 1.21 * 746 / 900 * 1.05;
    // the blocks from the tower's front legs back count as the city behind it
    const backDim = tower.z - 50 * TS;
    // Shiba Park round its foot (owner): trees instead of blocks
    // a rectangle of trees (owner: not round), 360 by 320 m, its sides along the streets
    const onRoad = ( x0: number, x1: number ) => this.roads.some( rx => x1 > rx - 21 && x0 < rx + 21 );

    // ---- the sky ----
    const sk = canvas();
    g = sk.g;
    {
      const gr = g.createLinearGradient( 0, 0, 0, hz );
      gr.addColorStop( 0, this.rgba( skyTop, 1 ) );
      gr.addColorStop( 0.7, this.rgba( this.mix( skyTop, skyLow, 0.6 ), 1 ) );
      gr.addColorStop( 1, this.rgba( skyLow, 1 ) );
      g.fillStyle = gr;
      g.fillRect( 0, 0, W, hz + 4 );
      // the city's glow on the low haze
      const hg = g.createLinearGradient( 0, hz - H * 0.08, 0, hz + 2 );
      hg.addColorStop( 0, this.rgba( glow, 0 ) );
      hg.addColorStop( 1, this.rgba( this.mix( glow, warm, 0.15 ), 0.55 ) );
      g.fillStyle = hg;
      g.fillRect( 0, hz - H * 0.08, W, H * 0.08 + 4 );
    }

    // ---- the ground: dark, its streets glowing ----
    const lamps: [ number, number, number ][] = [];
    let lampHead: ( x: number, z: number, big: number ) => void = () => undefined;
    // (owner: Tokyo stuttered) drawn over the sky on the same layer, as it was drawn over it each frame
    g = sk.g;
    {
      g.fillStyle = this.rgba( this.mix( body, glow, 0.15 ), 1 );
      g.fillRect( 0, hz, W, H - hz );
      // the far ground melting into the glow at the horizon
      const fg = g.createLinearGradient( 0, hz, 0, hz + H * 0.08 );
      fg.addColorStop( 0, this.rgba( skyLow, 1 ) );
      fg.addColorStop( 1, this.rgba( glow, 0 ) );
      g.fillStyle = fg;
      g.fillRect( 0, hz, W, H * 0.08 );
      // the streets (owner: nicer roads): dark asphalt between the blocks, lit by rows of street lamps
      // along both kerbs, the avenues wider and brighter, the side streets across them narrower
      // (owner: the lamps' order matters) each lamp is drawn with the city, at its own depth on its pole,
      // so a nearer block hides it and it shines over the farther ones; on the ground only its pool
      const lampDot = ( x: number, z: number, big: number ) => {
        lamps.push( [ x, z, big ] );
        const [ gx, gy ] = P( x, 0, z );
        const sg = F / z;
        const pr0 = Math.max( 0.8, sg * 5 * big );
        const pool = g.createRadialGradient( gx, gy, 0, gx, gy, pr0 );
        pool.addColorStop( 0, this.rgba( inAir( warm, z, 0.5 ), 0.22 ) );
        pool.addColorStop( 1, this.rgba( warm, 0 ) );
        g.fillStyle = pool;
        g.fillRect( gx - pr0, gy - pr0, pr0 * 2, pr0 * 2 );
      };
      lampHead = ( x: number, z: number, big: number ) => {
        const [ px, py ] = P( x, 7, z );
        const s = F / z;
        // a lamp's real size (owner: the big blobs spilled over the blocks): a small bright head, a short halo
        const rr = Math.max( 0.6, s * 0.9 * big );
        const lg = g.createRadialGradient( px, py, 0, px, py, rr * 2.6 );
        lg.addColorStop( 0, this.rgba( inAir( [ 255, 232, 184 ], z, 0.4 ), 1 ) );
        lg.addColorStop( 0.25, this.rgba( inAir( warm, z, 0.5 ), 0.55 ) );
        lg.addColorStop( 1, this.rgba( warm, 0 ) );
        // always at full light (owner: the lamps do not dim)
        g.fillStyle = lg;
        g.fillRect( px - rr * 2.6, py - rr * 2.6, rr * 5.2, rr * 5.2 );
      };
      const asphalt = ( z: number ) => this.rgba( inAir( this.mix( body, [ 60, 50, 50 ], 0.25 ), z, 0.7 ), 1 );
      // the side streets across, unlit (owner: lamps only on the avenues)
      for ( let z = 540; z < 9000; z *= 1.11 ) {
        const half = halfAt( z );
        const x1 = half;
        // (no street through the park: owner, the lights under the tower)
        const inParkZ = z + 9 > tower.z - 160 && z < tower.z + 160;
        const runs: [ number, number ][] = inParkZ ? [ [ -half, tower.x - 180 ], [ tower.x + 180, x1 ] ] : [ [ -half, x1 ] ];
        for ( const [ a, b ] of runs ) {
          poly( [ P( a, 0, z ), P( b, 0, z ), P( b, 0, z + 9 ), P( a, 0, z + 9 ) ], asphalt( z ) );
        }
      }
      // the avenues toward the horizon
      for ( const rx of this.roads ) {
        for ( let z = 480; z < 12000; z *= 1.04 ) {
          const z2 = z * 1.04;
          poly( [ P( rx - 19, 0, z ), P( rx + 19, 0, z ), P( rx + 19, 0, z2 ), P( rx - 19, 0, z2 ) ], asphalt( z ) );
        }
        for ( let z = 490; z < 9000; z += z < 2000 ? 30 : 60 ) {
          // (owner: the roads did not show) brighter lamps, one row along each kerb, none on the median
          lampDot( rx - 19, z, 1.4 ); lampDot( rx + 19, z + 15, 1.4 );
        }
      }
    }
    this.base = sk.c;

    // ---- the city ----
    const ct = canvas();
    g = ct.g;
    this.blinks = [];
    const things: Thing[] = [];
    const boxes: [ number, number, number, number, number ][] = [];
    const add = ( z: number, draw: () => void ) => things.push( { z, draw } );

    // a building: its front and the side toward the axis, its roof seen from above, its windows
    const building = ( x0: number, x1: number, z0: number, d: number, h: number, seed: number, kind: number ) => {
      const br = this.seededLocal( seed );
      const s = F / z0;
      const tone = inAir( this.mix( body, kind === 2 ? [ 40, 42, 64 ] : kind === 1 ? [ 20, 22, 32 ] : [ 14, 15, 24 ], 0.3 ), z0, 0.6 );
      const front = this.rgba( tone, 1 ), side = this.rgba( this.mix( tone, [ 0, 0, 0 ], 0.25 ), 1 );
      // the roofs dark too (owner), only a breath of the sky's glow on them
      const roof = this.rgba( this.mix( tone, glow, 0.07 ), 1 );
      // the side seen from the axis
      if ( x0 > 0 ) { poly( [ P( x0, 0, z0 ), P( x0, 0, z0 + d ), P( x0, h, z0 + d ), P( x0, h, z0 ) ], side ); }
      if ( x1 < 0 ) { poly( [ P( x1, 0, z0 ), P( x1, 0, z0 + d ), P( x1, h, z0 + d ), P( x1, h, z0 ) ], side ); }
      poly( [ P( x0, 0, z0 ), P( x1, 0, z0 ), P( x1, h, z0 ), P( x0, h, z0 ) ], front );
      // the roof, from above
      poly( [ P( x0, h, z0 ), P( x1, h, z0 ), P( x1, h, z0 + d ), P( x0, h, z0 + d ) ], roof );
      erase( [ P( x0, 0, z0 ), P( x1, 0, z0 ), P( x1, h, z0 ), P( x1, h, z0 + d ), P( x0, h, z0 + d ), P( x0, h, z0 ) ] );
      if ( x0 > 0 ) { erase( [ P( x0, 0, z0 ), P( x0, 0, z0 + d ), P( x0, h, z0 + d ), P( x0, h, z0 ) ] ); }
      if ( x1 < 0 ) { erase( [ P( x1, 0, z0 ), P( x1, 0, z0 + d ), P( x1, h, z0 + d ), P( x1, h, z0 ) ] ); }
      // what stands on the roof (owner: the roofs were bare): plant rooms, water tanks, antennas
      if ( s > 0.5 ) {
        const box = ( bx0: number, bz0: number, bw: number, bd: number, bh: number, shade: number ) => {
          const c = this.mix( tone, glow, 0.05 + shade );
          if ( bx0 > 0 ) { poly( [ P( bx0, h, bz0 ), P( bx0, h, bz0 + bd ), P( bx0, h + bh, bz0 + bd ), P( bx0, h + bh, bz0 ) ], this.rgba( this.mix( c, [ 0, 0, 0 ], 0.3 ), 1 ) ); }
          if ( bx0 + bw < 0 ) { poly( [ P( bx0 + bw, h, bz0 ), P( bx0 + bw, h, bz0 + bd ), P( bx0 + bw, h + bh, bz0 + bd ), P( bx0 + bw, h + bh, bz0 ) ], this.rgba( this.mix( c, [ 0, 0, 0 ], 0.3 ), 1 ) ); }
          poly( [ P( bx0, h, bz0 ), P( bx0 + bw, h, bz0 ), P( bx0 + bw, h + bh, bz0 ), P( bx0, h + bh, bz0 ) ], this.rgba( this.mix( c, [ 0, 0, 0 ], 0.15 ), 1 ) );
          poly( [ P( bx0, h + bh, bz0 ), P( bx0 + bw, h + bh, bz0 ), P( bx0 + bw, h + bh, bz0 + bd ), P( bx0, h + bh, bz0 + bd ) ], this.rgba( this.mix( c, glow, 0.25 ), 1 ) );
        };
        const w = x1 - x0;
        const nb = 1 + Math.floor( br() * 3 );
        for ( let q = 0; q < nb; q++ ) {
          const bw = Math.min( w * 0.5, 3 + br() * 7 ), bd = Math.min( d * 0.5, 3 + br() * 6 );
          box( x0 + br() * ( w - bw ), z0 + br() * ( d - bd ), bw, bd, 2 + br() * 3.5, br() * 0.1 );
        }
        if ( br() < 0.35 ) {
          // a water tank on legs
          const cx = x0 + 2 + br() * ( w - 4 ), cz = z0 + 2 + br() * ( d - 4 ), tr2 = 1.4 + br();
          const [ ex, ey ] = P( cx, h + 4.5, cz ), [ , eb ] = P( cx, h + 1.5, cz );
          const rx = F * tr2 / cz, ry = Math.max( 0.5, rx * 0.35 );
          g.fillStyle = this.rgba( this.mix( tone, glow, 0.2 ), 1 );
          g.fillRect( ex - rx, ey, rx * 2, eb - ey );
          g.beginPath(); g.ellipse( ex, eb, rx, ry, 0, 0, Math.PI ); g.fill();
          g.fillStyle = this.rgba( this.mix( tone, glow, 0.38 ), 1 );
          g.beginPath(); g.ellipse( ex, ey, rx, ry, 0, 0, Math.PI * 2 ); g.fill();
        }
        if ( br() < 0.45 ) {
          // an antenna or two, thin, a small light at the tip
          for ( let q = 0; q < ( br() < 0.3 ? 2 : 1 ); q++ ) {
            const ax = x0 + br() * w, az = z0 + br() * d, ah = 4 + br() * 10;
            const a0 = P( ax, h, az ), a1 = P( ax, h + ah, az );
            g.strokeStyle = this.rgba( this.mix( tone, glow, 0.45 ), 0.9 );
            g.lineWidth = Math.max( 0.5, s * 0.25 );
            g.beginPath(); g.moveTo( a0[ 0 ], a0[ 1 ] ); g.lineTo( a1[ 0 ], a1[ 1 ] ); g.stroke();
            g.fillStyle = br() < 0.6 ? 'rgba(255,90,80,0.85)' : 'rgba(240,244,255,0.8)';
            g.fillRect( a1[ 0 ] - Math.max( 0.5, s * 0.3 ), a1[ 1 ] - Math.max( 0.5, s * 0.3 ), Math.max( 1, s * 0.6 ), Math.max( 1, s * 0.6 ) );
          }
        }
      }
      // the windows: rows of them, the lit ones warm, white or cool, a few whole floors lit
      const floorH = 3.6, colW = kind === 2 ? 2.2 : 3.2;
      const floors = Math.floor( h / floorH ), cols = Math.max( 1, Math.floor( ( x1 - x0 ) / colW ) );
      // far off a face's windows are single specks: only as many as read as its light
      // the towers lit on every floor (owner), the low blocks more patchily
      const litShare = h > 90 ? 0.6 + br() * 0.3 : 0.25 + br() * 0.45;
      const tint = br() < 0.55 ? warm : br() < 0.6 ? coolLit : whiteLit;
      const tiny = s * colW * 0.55 < 1.3;
      // far off only some of the lit windows are drawn, spread over every floor, never just the low ones
      const keep = tiny ? Math.min( 1, ( h > 90 ? 520 : 200 ) / Math.max( 1, floors * cols * litShare ) ) : 1;
      // (owner: Tokyo stuttered) a face's windows filled by colour and strength, not each with its own colour
      // made anew (near two million small fills made the scene take well over a second): the ones large enough
      // that no two share a pixel gathered into a few paths filled at once; the small far ones still one by
      // one, so their light adds up where they crowd as it did; each window's strength in eight steps
      const faceWindows = ( fx0: number, fx1: number, fz0: number, fz1: number, dim: number ) => {
        const n = Math.max( 1, Math.floor( Math.hypot( fx1 - fx0, fz1 - fz0 ) / colW ) );
        const groups = new Map<number, Path2D>(), styles = new Map<number, string>(), steps = 8;
        const style = ( key: number ) => {
          let st = styles.get( key );
          if ( !st ) { st = this.rgba( inAir( key >= 100 ? whiteLit : tint, z0, 0.5 ), dim * ( 0.65 + ( key % 100 ) / steps * 0.35 ) ); styles.set( key, st ); }
          return st;
        };
        // dim in the still city, full in the bright layer
        // the city behind the tower dimmer still at rest (owner: the words over it were hard to read)
        const dimA = z0 >= backDim ? 0.22 : 0.4;
        let last = -1;
        for ( let f = 0; f < floors; f++ ) {
          const floorLit = br() < 0.12;
          for ( let c = 0; c < n; c++ ) {
            const on = floorLit || br() < litShare;
            if ( !on || br() > keep ) { continue; }
            const u = ( c + 0.5 ) / n;
            const [ px, py ] = P( fx0 + ( fx1 - fx0 ) * u, f * floorH + floorH * 0.5, fz0 + ( fz1 - fz0 ) * u - 0.1 );
            const sz = F / ( fz0 + ( fz1 - fz0 ) * u );
            const ww = Math.max( 0.6, sz * colW * 0.55 * ( fz1 !== fz0 ? 0.5 : 1 ) ), hh = Math.max( 0.5, sz * floorH * 0.45 );
            const key = ( floorLit ? 100 : 0 ) + Math.round( br() * steps );
            this.winTop = Math.min( this.winTop, py - hh / 2 );
            this.winBottom = Math.max( this.winBottom, py + hh / 2 );
            if ( ww >= 2 && hh >= 2 ) {
              let path = groups.get( key );
              if ( !path ) { path = new Path2D(); groups.set( key, path ); }
              path.rect( px - ww / 2, py - hh / 2, ww, hh );
              continue;
            }
            if ( key !== last ) { const st = style( key ); g.fillStyle = st; if ( wg ) { wg.fillStyle = st; } last = key; }
            g.globalAlpha = dimA;
            g.fillRect( px - ww / 2, py - hh / 2, ww, hh );
            g.globalAlpha = 1;
            if ( wg ) { wg.fillRect( px - ww / 2, py - hh / 2, ww, hh ); }
          }
        }
        for ( const [ key, path ] of groups ) {
          const st = style( key );
          g.globalAlpha = dimA;
          g.fillStyle = st;
          g.fill( path );
          g.globalAlpha = 1;
          if ( wg ) { wg.fillStyle = st; wg.fill( path ); }
        }
      };
      faceWindows( x0, x1, z0, z0, 1 );
      if ( x0 > 0 ) { faceWindows( x0, x0, z0, z0 + d, 0.65 ); }
      if ( x1 < 0 ) { faceWindows( x1, x1, z0, z0 + d, 0.65 ); }
      // the tall ones carry red aircraft lights on their roofs
      if ( h > 130 ) {
        const [ ax, ay ] = P( ( x0 + x1 ) / 2, h + 1, z0 + d / 2 );
        this.blinks.push( [ ax, ay, br() * 6, Math.max( 1, s * 1.2 ) ] );
      }
    };

    // Tokyo Tower: its four legs in 3D, the lattice between them in its orange light, the arch at its
    // foot, the main deck and the top deck in white, the mast in bands; the back face hazier behind
    const tokyoTower = () => {
      const { x: tx, z: tz } = tower;
      // the tower larger than life (owner: 10%, then 10% more), about its foot
      const P = ( x: number, y: number, z: number ) => this.P( tx + ( x - tx ) * TS, y * TS, tz + ( z - tz ) * TS );
      // half-width of its square section with height (m), after the real one (owner: learn its true
      // curve): the legs fall away in one smooth exponential, as an Eiffel-type tower's do, from 10.5 m at
      // the main deck to 3.5 m at the top deck; a little wider at the foot than the real 80 m (owner)
      const wAt = ( y: number ) => 48 * Math.exp( -Math.min( y, 262 ) / 96 );
      // drawn white: the light's colour comes in each frame (drawTower)
      const orange: RGB = [ 255, 255, 255 ], hot: RGB = [ 255, 255, 255 ];
      const levels: number[] = [];
      for ( let y = 0; y < 262; ) { levels.push( y ); y += y < 60 ? 9 : y < 160 ? 8 : 7; }
      levels.push( 262 );
      // one face of the lattice at depth dz from the tower's middle (front: -w, back: +w)
      const face = ( sideZ: 1 | -1, alpha: number, widthK: number ) => {
        const pt = ( u: number, y: number ) => { const w = wAt( y ); return P( tx + u * w, y, tz + sideZ * w ); };
        const line = ( a: [ number, number ], b: [ number, number ], lw: number ) => { g.lineWidth = lw; g.beginPath(); g.moveTo( a[ 0 ], a[ 1 ] ); g.lineTo( b[ 0 ], b[ 1 ] ); g.stroke(); };
        const s = F / tz;
        for ( const pass of [ 0, 1 ] ) {
          // a soft glow under, the bright iron over
          g.strokeStyle = pass === 0 ? this.rgba( orange, 0.22 * alpha ) : this.rgba( this.mix( orange, hot, 0.25 ), alpha );
          const k = pass === 0 ? 3.2 : 1;
          // the two corner legs
          for ( const u of [ -1, 1 ] ) {
            for ( let i = 0; i < levels.length - 1; i++ ) { line( pt( u, levels[ i ] ), pt( u, levels[ i + 1 ] ), Math.max( 0.8, s * 1.6 * widthK * k ) ); }
          }
          // the level girders and the crossing diagonals of each panel, open under the arch
          for ( let i = 0; i < levels.length - 1; i++ ) {
            const y0 = levels[ i ], y1 = levels[ i + 1 ];
            const archTop = 62;
            if ( y0 >= archTop ) { line( pt( -1, y0 ), pt( 1, y0 ), Math.max( 0.5, s * 0.7 * widthK * k ) ); }
            const subs = y0 < 120 ? 4 : y0 < 200 ? 2 : 1;
            for ( let q = 0; q < subs; q++ ) {
              const ua = -1 + 2 * q / subs, ub = -1 + 2 * ( q + 1 ) / subs;
              if ( y1 <= archTop && ua > -0.6 && ub < 0.6 ) { continue; }
              line( pt( ua, y0 ), pt( ub, y1 ), Math.max( 0.4, s * 0.5 * widthK * k ) );
              line( pt( ub, y0 ), pt( ua, y1 ), Math.max( 0.4, s * 0.5 * widthK * k ) );
            }
          }
          // the arch between the legs
          g.lineWidth = Math.max( 0.6, s * 1.1 * widthK * k );
          g.beginPath();
          for ( let q = 0; q <= 24; q++ ) {
            const u = -1 + 2 * q / 24;
            // more open (owner): it springs from the feet and rises full to 62 m
            const y = 2 + ( 62 - 2 ) * Math.pow( 1 - u * u, 0.62 );
            const [ px, py ] = pt( u * 0.94, y );
            q ? g.lineTo( px, py ) : g.moveTo( px, py );
          }
          g.stroke();
        }
      };
      const keep = g;
      const mask = canvas();
      towerMask = mask.c;
      // the glow round the tower is the veil's colour, drawn each frame (drawTower)
      g = mask.g;
      this.towerGlow = [ ...P( tx, 140, tz ), F * 190 / tz ];
      g = mask.g;
      face( 1, 0.55, 0.8 );
      // a deck (owner: in the tower's colours, not white): its body dark iron on the mask, its band of
      // windows and the veil's points along its edges lit in the tower's colour each frame (drawTower)
      const deck = ( y0: number, y1: number, w: number ) => {
        const a = P( tx - w, y0, tz - w ), b = P( tx + w, y0, tz - w ), c = P( tx + w, y1, tz - w ), d = P( tx - w, y1, tz - w );
        const top0 = P( tx - w, y1, tz + w ), top1 = P( tx + w, y1, tz + w );
        poly( [ d, c, top1, top0 ], this.rgba( white, 1 ) );
        poly( [ a, b, c, d ], this.rgba( white, 1 ) );
        const m0 = P( tx - w, y0 + ( y1 - y0 ) * 0.3, tz - w - 0.1 ), m1 = P( tx + w, y0 + ( y1 - y0 ) * 0.7, tz - w - 0.1 );
        this.deckBand = [ m0[ 0 ], m1[ 1 ], m1[ 0 ], m0[ 1 ], F / tz * TS ];
        const s0 = F / tz;
        for ( const yy of [ y0 + 0.6, y1 - 0.6 ] ) {
          for ( let q = 0; q <= 16; q++ ) {
            const [ px, py ] = P( tx - w + 2 * w * q / 16, yy, tz - w - 0.1 );
            this.veil.push( [ px, py, 1, Math.max( 0.6, s0 * 0.55 ) ] );
          }
        }
      };
      face( -1, 1, 1 );
      // the Diamond Veil's LED points (owner's photograph: point by point, on every member): along the
      // legs, the girders and the diagonals of the front face, from the arch up
      this.veil = [];
      {
        const s0 = F / tz;
        // the back legs lit too (owner), a little smaller and fewer, behind
        const pt = ( u: number, y: number ) => { const w = wAt( y ); return P( tx + u * w, y, tz - w ); };
        const push = ( p: [ number, number ], y: number ) => this.veil.push( [ p[ 0 ], p[ 1 ], y / 250, Math.max( 0.6, s0 * 0.55 ) ] );
        for ( let y = 4; y < 248; y += 3.2 ) {
          const w = wAt( y );
          for ( const u of [ -1, 1 ] ) {
            const [ px, py ] = P( tx + u * w, y, tz + w );
            this.veil.push( [ px, py, y / 250, Math.max( 0.5, s0 * 0.45 ) ] );
          }
        }
        for ( let i = 0; i < levels.length - 1; i++ ) {
          const y0 = levels[ i ], y1 = levels[ i + 1 ];
          if ( y0 >= 250 ) { continue; }
          for ( const u of [ -1, 1 ] ) { for ( let q = 0; q < 3; q++ ) { const y = y0 + ( y1 - y0 ) * q / 3; push( pt( u, y ), y ); } }
          const subs = y0 < 120 ? 4 : y0 < 200 ? 2 : 1;
          for ( let q = 0; q < subs; q++ ) {
            const ua = -1 + 2 * q / subs, ub = -1 + 2 * ( q + 1 ) / subs;
            if ( y1 <= 62 && ua > -0.6 && ub < 0.6 ) { continue; }
            for ( let k2 = 1; k2 < 4; k2++ ) {
              const f2 = k2 / 4, y = y0 + ( y1 - y0 ) * f2;
              push( pt( ua + ( ub - ua ) * f2, y ), y );
              push( pt( ub + ( ua - ub ) * f2, y ), y );
            }
            push( pt( ua, y0 ), y0 );
          }
        }
      }
      g = mask.g;
      deck( 145, 160, 13 );
      // the top deck dark, lit by the veil's points like the photograph's, not white
      g = mask.g;
      {
        const w = 4.6;
        poly( [ P( tx - w, 248, tz - w ), P( tx + w, 248, tz - w ), P( tx + w, 255, tz - w ), P( tx - w, 255, tz - w ) ], this.rgba( white, 1 ) );
        const s0 = F / tz;
        for ( let yy = 248.5; yy < 255; yy += 1.6 ) {
          for ( let q = 0; q <= 6; q++ ) {
            const [ px, py ] = P( tx - w + 2 * w * q / 6, yy, tz - w - 0.1 );
            this.veil.push( [ px, py, 1, Math.max( 0.6, s0 * 0.55 ) ] );
          }
        }
      }
      g = mask.g;
      // the mast: dark iron to its tip, the veil's points on its foot, its light above (drawTower)
      {
        const s = F / tz;
        for ( let y = 254; y < 333; y += 8 ) {
          const w = 2.2 - ( y - 254 ) / 79 * 1.7;
          const a = P( tx - w, y, tz ), b = P( tx + w, Math.min( 333, y + 8 ), tz );
          mask.g.fillStyle = this.rgba( white, 1 );
          mask.g.fillRect( a[ 0 ], b[ 1 ], Math.max( 0.8, b[ 0 ] - a[ 0 ] ), a[ 1 ] - b[ 1 ] );
        }
        for ( let y = 256; y < 282; y += 2.2 ) {
          for ( const u of [ -1, 1 ] ) {
            const [ px, py ] = P( tx + u * ( 2.2 - ( y - 254 ) / 79 * 1.7 ), y, tz - 0.1 );
            this.veil.push( [ px, py, 1, Math.max( 0.6, s * 0.5 ) ] );
          }
        }
        const tip = P( tx, 333, tz );
        this.blinks.push( [ tip[ 0 ], tip[ 1 ], 1.3, Math.max( 1.2, s * 1.4 ) ] );
        // the mast lit above the veil's points nearly to its tip, as in the photograph (drawTower)
        const mb = P( tx, 282, tz ), mt = P( tx, 326, tz );
        this.mastBar = [ mb[ 0 ], mb[ 1 ], mt[ 1 ], Math.max( 1.2, s * 2.4 ), Math.max( 0.6, s * 0.7 ) ];
      }
      g = keep;
    };

    // the park's trees, drawn as Paris's and Rio's were (owner): leaves painted once per tone, a crown of
    // crisp clumps and real leaves over it, the top lit by the city's glow, the underside by the lamps
    const leafCache = new Map<string, HTMLCanvasElement>();
    const leaf = ( tint: RGB ) => {
      const key = tint.map( v => Math.round( v / 6 ) ).join( ',' );
      const hit = leafCache.get( key );
      if ( hit ) { return hit; }
      const size = 40;
      const cv = document.createElement( 'canvas' );
      cv.width = cv.height = size;
      const lg = cv.getContext( '2d' )!;
      lg.translate( size / 2, size / 2 );
      lg.beginPath();
      for ( let q = 0; q <= 10; q++ ) {
        const a = -Math.PI / 2 + q / 10 * Math.PI * 2;
        const rr = q % 2 ? size * 0.2 : size * ( q === 0 || q === 10 ? 0.46 : 0.4 );
        const px = Math.cos( a ) * rr, py = Math.sin( a ) * rr * 0.92;
        q ? lg.quadraticCurveTo( Math.cos( a - 0.3 ) * rr * 1.05, Math.sin( a - 0.3 ) * rr, px, py ) : lg.moveTo( px, py );
      }
      lg.closePath();
      const fg = lg.createRadialGradient( 0, 0, 0, 0, 0, size * 0.45 );
      fg.addColorStop( 0, this.rgba( this.mix( tint, [ 6, 10, 12 ], 0.2 ), 1 ) );
      fg.addColorStop( 1, this.rgba( this.mix( tint, glow, 0.15 ), 1 ) );
      lg.fillStyle = fg;
      lg.fill();
      leafCache.set( key, cv );
      return cv;
    };
    const parkTree = ( x: number, z: number, hgt: number, base: RGB ) => {
      const s = F / z;
      const [ cx, cy ] = P( x, hgt * 0.7, z );
      const r = Math.max( 0.8, s * hgt * 0.32 );
      const rnd = this.seededLocal( Math.floor( x * 13 + z * 7 ) );
      const dark = this.mix( base, [ 4, 8, 10 ], 0.5 );
      const n = Math.min( 16, 6 + Math.floor( r / 3 ) );
      const clumps: [ number, number, number ][] = [];
      for ( let q = 0; q < n; q++ ) {
        const a = rnd() * Math.PI * 2, d = Math.sqrt( rnd() ) * r * 0.6;
        clumps.push( [ cx + Math.cos( a ) * d * 1.1, cy + Math.sin( a ) * d * 0.78, r * ( 0.4 + rnd() * 0.2 ) ] );
      }
      g.fillStyle = this.rgba( dark, 1 );
      g.beginPath();
      for ( const [ qx, qy, qr ] of clumps ) { g.moveTo( qx + qr, qy ); g.arc( qx, qy, qr, 0, Math.PI * 2 ); }
      g.fill();
      if ( wg ) {
        wg.globalCompositeOperation = 'destination-out';
        wg.fillStyle = '#000';
        wg.beginPath();
        for ( const [ qx, qy, qr ] of clumps ) { wg.moveTo( qx + qr, qy ); wg.arc( qx, qy, qr * 1.1, 0, Math.PI * 2 ); }
        wg.fill();
        wg.globalCompositeOperation = 'source-over';
      }
      const lit = this.mix( base, this.mix( glow, peri, 0.4 ), 0.35 ), lamp = this.mix( base, warm, 0.3 );
      const count = Math.min( 420, Math.floor( r * r * 0.5 ) );
      const ls = Math.max( 1.6, r * 0.2 );
      for ( let q = 0; q < count; q++ ) {
        const [ qx, qy, qr ] = clumps[ Math.floor( rnd() * clumps.length ) ];
        const a = rnd() * Math.PI * 2, d = Math.sqrt( rnd() ) * qr * 0.95;
        const lx = qx + Math.cos( a ) * d, ly = qy + Math.sin( a ) * d;
        const up = ( cy - ly ) / r;
        const depth = q / count;
        const tone = up > 0.2 ? this.mix( base, lit, Math.min( 1, up ) * 0.8 ) : up < -0.35 ? this.mix( base, lamp, Math.min( 1, -up ) * 0.6 ) : this.mix( base, dark, 0.35 * ( 1 - depth ) );
        const sz = ls * ( 0.75 + rnd() * 0.5 );
        g.save();
        g.translate( lx, ly );
        g.rotate( rnd() * Math.PI * 2 );
        g.drawImage( leaf( tone ), -sz / 2, -sz / 2, sz, sz );
        g.restore();
      }
    };
    // the city: rows of blocks and towers far to near, the tallest gathered in clusters
    // the high-rise districts lie off beyond the tower, as from Roppongi; near us the blocks stay low
    const clusters: [ number, number, number ][] = [ [ -1500, 2400, 700 ], [ 1100, 2300, 600 ], [ -2900, 3900, 1000 ], [ 2700, 3600, 900 ], [ 300, 5200, 1400 ], [ -650, 1500, 260 ] ];
    const heightAt = ( x: number, z: number, rr: () => number ) => {
      let lift = 0;
      for ( const [ cx, cz, rad ] of clusters ) { const d2 = ( ( x - cx ) ** 2 + ( z - cz ) ** 2 ) / ( rad * rad ); lift = Math.max( lift, Math.exp( -d2 ) ); }
      // (owner) the far towers kept lower, the near ones and those to the sides before us taller; the
      // near ones stand before Tokyo Tower (they are drawn after it)
      // the middle before the tower stays low so it shows (only the sides rise)
      const mid = z < 1200 && Math.abs( x ) <= 200;
      const side = z < 1200 && !mid ? 0.3 : 0;
      // (owner, later: the ones before the tower taller too, still under it)
      const base = mid ? 12 + rr() * 18 + ( rr() < 0.12 ? 10 + rr() * 18 : 0 ) : 12 + rr() * 24;
      const far = z > 3200 ? 0.6 : z > 1900 ? 0.8 : 1.15;
      const tall = !mid && rr() < 0.1 + side + lift * 0.55;
      return ( tall ? base * 1.3 + ( 50 + rr() * 200 ) * ( 0.4 + lift * 0.8 ) : base * 1.2 ) * far;
    };
    // (owner: fewer rows before the tower) the nearest rows left out
    for ( let row = 0, z = 14000; z > 470; row++, z *= 0.955 ) {
      const half = halfAt( z );
      const rr = this.seededLocal( 500 + row + this.citySeed * 131 );
      let x = -half;
      while ( x < half ) {
        const w = 18 + rr() * 34, d = 18 + rr() * 26;
        let x0 = x, x1 = x + w;
        const zz = z * ( 1 + ( rr() - 0.5 ) * 0.02 );
        x += w + 6 + rr() * 14;
        // (owner) the blocks either side of an avenue stand in one line along its kerb
        for ( const rx of this.roads ) {
          if ( x1 > rx - 61 && x1 < rx - 21 ) { const dx = rx - 21 - x1; x0 += dx; x1 += dx; }
          else if ( x0 > rx + 21 && x0 < rx + 61 ) { const dx = rx + 21 - x0; x0 += dx; x1 += dx; }
        }
        if ( onRoad( x0, x1 ) ) { continue; }
        // the park round the tower (owner: the blocks start right where it ends, no gap, none reaching
        // into it): a block that would cross its edge is cut back to the edge
        let zz2 = zz, d2 = d;
        if ( x1 > tower.x - 180 && x0 < tower.x + 180 && zz + d > tower.z - 160 && zz < tower.z + 160 ) {
          if ( zz < tower.z - 160 ) { d2 = tower.z - 160 - zz; }
          else if ( zz + d > tower.z + 160 && zz > tower.z + 100 ) { zz2 = tower.z + 160; d2 = zz + d - zz2; }
          else if ( x0 < tower.x - 180 ) { x1 = tower.x - 180; }
          else if ( x1 > tower.x + 180 ) { x0 = tower.x + 180; }
          else { continue; }
          if ( x1 - x0 < 8 || d2 < 8 ) { continue; }
        }
        let h = heightAt( x0, zz2, rr );
        // the blocks on an avenue's near side (toward the axis) kept low so the avenue shows (owner)
        for ( const rx of this.roads ) {
          if ( rx < 0 ? x0 >= rx + 21 && x0 < rx + 90 : x1 <= rx - 21 && x1 > rx - 90 ) { h = Math.min( h, 22 + ( ( x0 * 7 ) % 9 ) ); }
        }
        // (owner) nothing before the tower's legs rises over its foot: those blocks are cut down below it
        if ( zz2 < tower.z && x1 > tower.x - 150 && x0 < tower.x + 150 ) {
          const footY = this.P( 0, 0, tower.z - 50 * TS )[ 1 ];
          h = Math.min( h, Math.max( 8, this.eye - ( footY - hz ) * zz2 / F - 4 ) );
        }
        const seed = Math.floor( rr() * 1e6 ), kind = h > 120 ? 2 : rr() < 0.5 ? 1 : 0;
        const bx0 = x0, bx1 = x1;
        boxes.push( [ bx0, bx1, zz2, zz2 + d2, h ] );
        add( zz2, () => building( bx0, bx1, zz2, d2, h, seed, kind ) );
      }
    }
    // the park's trees: dark crowns in the night, their tops catching the city's glow (owner: no lamps)
    // the plaza under the tower: dark paving, a little lighter than the night ground (owner)
    add( tower.z + 200, () => {
      const w = 50, d = 54;
      const pv = this.mix( body, glow, 0.1 );
      poly( [ P( tower.x - w, 0, tower.z - d ), P( tower.x + w, 0, tower.z - d ), P( tower.x + w, 0, tower.z + d ), P( tower.x - w, 0, tower.z + d ) ], this.rgba( pv, 1 ) );
      const pr2 = this.seededLocal( 43 );
      for ( let q = 0; q < 160; q++ ) {
        const [ px, py ] = P( tower.x + ( pr2() - 0.5 ) * 2 * w, 0, tower.z + ( pr2() - 0.5 ) * 2 * d );
        g.fillStyle = this.rgba( this.mix( pv, pr2() < 0.5 ? [ 0, 0, 0 ] : glow, 0.2 ), 0.5 );
        g.fillRect( px, py, Math.max( 1, F * 2 / tower.z ), Math.max( 0.5, F * 0.7 / tower.z ) );
      }
    } );
    {
      const pr = this.seededLocal( 41 );
      const base = this.mix( this.mix( green, navy, 0.62 ), [ 10, 22, 18 ], 0.15 );
      for ( let z = tower.z + 175; z > tower.z - 175; z -= 7 ) {
        for ( let x = tower.x - 195; x < tower.x + 195; x += 7 ) {
          const xx = x + ( pr() - 0.5 ) * 6, zz = z + ( pr() - 0.5 ) * 6;
          // kept a crown's width inside the park's edge, so no tree reaches over a block
          if ( Math.abs( xx - tower.x ) > 172 || Math.abs( zz - tower.z ) > 154 || pr() < 0.12 ) { continue; }
          // the tower's own plaza stays open
          if ( Math.abs( xx - tower.x ) < 48 && Math.abs( zz - tower.z ) < 52 ) { continue; }
          const hh = 12 + pr() * 7, tint = this.mix( base, pr() < 0.5 ? [ 30, 60, 40 ] : [ 40, 56, 60 ], 0.25 );
          add( zz, () => {
            parkTree( xx, zz, hh, tint );
          } );
        }
      }
    }
    add( tower.z, tokyoTower );
    things.sort( ( a, b ) => b.z - a.z );
    // the city behind the tower on one canvas, before it on another
    const fr = canvas();
    const wb = canvas(), wf = canvas();
    wg = wb.g;
    // (owner: what stands before the tower is drawn after it) the split at the front legs' foot
    const frontFoot = tower.z - 50 * TS;
    for ( const t of things ) {
      if ( t.z < frontFoot ) { g = fr.g; wg = wf.g; }
      t.draw();
    }
    // the avenues' lamps last (owner: only on the road, never over a roof): each one is drawn only if no
    // block stands on the line from the eye to it, found through a grid of the blocks
    {
      const cell = 100;
      const grid = new Map<string, number[]>();
      boxes.forEach( ( [ x0, x1, z0, z1 ], i ) => {
        for ( let cx = Math.floor( x0 / cell ); cx <= Math.floor( x1 / cell ); cx++ ) {
          for ( let cz = Math.floor( z0 / cell ); cz <= Math.floor( z1 / cell ); cz++ ) {
            const key = cx + ',' + cz;
            const list = grid.get( key );
            list ? list.push( i ) : grid.set( key, [ i ] );
          }
        }
      } );
      const eye = this.eye;
      const blocked = ( lx: number, ly: number, lz: number ) => {
        const seen = new Set<number>();
        for ( let z = Math.max( 300, lz * 0.2 ); z < lz; z += cell / 2 ) {
          const x = lx * z / lz;
          for ( let dx = -1; dx <= 1; dx++ ) {
            const list = grid.get( ( Math.floor( x / cell ) + dx ) + ',' + Math.floor( z / cell ) );
            if ( !list ) { continue; }
            for ( const i of list ) {
              if ( seen.has( i ) ) { continue; }
              seen.add( i );
              const [ x0, x1, z0, z1, h ] = boxes[ i ];
              // the ray from the eye (0, eye, 0) to the lamp, against the block's box (slabs)
              let t0 = 0, t1 = 0.995;
              for ( const [ o, dd, lo, hi ] of [ [ 0, lx, x0, x1 ], [ eye, ly - eye, 0, h ], [ 0, lz, z0, z1 ] ] ) {
                if ( Math.abs( dd ) < 1e-9 ) { if ( o < lo || o > hi ) { t0 = 1; break; } continue; }
                let ta = ( lo - o ) / dd, tb = ( hi - o ) / dd;
                if ( ta > tb ) { const tmp = ta; ta = tb; tb = tmp; }
                t0 = Math.max( t0, ta ); t1 = Math.min( t1, tb );
                if ( t0 > t1 ) { break; }
              }
              if ( t0 <= t1 ) { return true; }
            }
          }
        }
        return false;
      };
      for ( const [ lx, lz, lb ] of lamps ) {
        if ( blocked( lx, 7, lz ) ) { continue; }
        g = lz < frontFoot ? fr.g : ct.g;
        lampHead( lx, lz, lb );
      }
    }
    this.city = ct.c;
    this.cityFront = fr.c;
    this.winBack = wb.c;
    this.winFront = wf.c;
    wg = null;
    // the tower's iron in its near black, cut to the tower's box: its legs' spread at the foot, its mast's tip
    this.towerLayer = null;
    if ( towerMask ) {
      const m: HTMLCanvasElement = towerMask;
      let x0 = W, y0 = H, x1 = 0, y1 = 0;
      for ( const cx of [ -60, 60 ] ) { for ( const cy of [ -2, 345 ] ) { for ( const cz of [ -60, 60 ] ) {
        const [ px, py ] = this.P( tower.x + cx * TS, cy * TS, tower.z + cz * TS );
        x0 = Math.min( x0, px ); x1 = Math.max( x1, px ); y0 = Math.min( y0, py ); y1 = Math.max( y1, py );
      } } }
      x0 = Math.max( 0, Math.floor( x0 - 12 ) ); y0 = Math.max( 0, Math.floor( y0 - 12 ) );
      x1 = Math.min( W, Math.ceil( x1 + 12 ) ); y1 = Math.min( H, Math.ceil( y1 + 12 ) );
      if ( x1 > x0 && y1 > y0 ) {
        const tl = document.createElement( 'canvas' );
        tl.width = Math.ceil( ( x1 - x0 ) * scale );
        tl.height = Math.ceil( ( y1 - y0 ) * scale );
        const tg = tl.getContext( '2d' )!;
        tg.drawImage( m, x0 * scale, y0 * scale, tl.width, tl.height, 0, 0, tl.width, tl.height );
        tg.globalCompositeOperation = 'source-in';
        tg.fillStyle = this.rgba( [ 30, 26, 40 ], 1 );
        tg.fillRect( 0, 0, tl.width, tl.height );
        this.towerLayer = tl;
        this.towerRect = [ x0, y0, x1 - x0, y1 - y0 ];
      }
    }
    this.cityTop = 0;
    this.frontTop = 0;
    this.winTop = Math.max( 0, this.winTop - 2 );
    this.winBottom = Math.min( H, this.winBottom + 2 );
  }

  private seededLocal( seed: number ): () => number {
    let s = ( seed * 2654435761 ) >>> 0;
    return () => {
      s = ( s + 0x6D2B79F5 ) >>> 0;
      let t = Math.imul( s ^ ( s >>> 15 ), 1 | s );
      t = ( t + Math.imul( t ^ ( t >>> 7 ), 61 | t ) ) ^ t;
      return ( ( t ^ ( t >>> 14 ) ) >>> 0 ) / 4294967296;
    };
  }

  /** The colour circle's five, or Tokyo's own: navy, periwinkle, neon pink, green, cyan. */
  private five(): RGB[] {
    if ( this.paletteColors.length >= 5 ) { return this.paletteColors.slice( 0, 5 ); }
    return [ [ 42, 48, 81 ], [ 114, 133, 220 ], [ 236, 161, 235 ], [ 92, 193, 157 ], [ 93, 200, 214 ] ];
  }
}
